// External libraries
import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-expect-error untyped-import
import { safeTrim } from '@checkout/react-components';
import { SelectionMenu } from '@paypalcorp/pp-react-selection-menu-weasley-v7';

// Internal modules
import { getContent, getLocale } from '@/lib';
import {
  logNationalityTextOnRenderFPTI,
  logSelectNationalityFPTI,
} from './fpti';
import {
  EditButton,
  Flex,
  NationalitySpan,
  SelectionMenuContainer,
} from './StyledComponents';
import { LoadingElement, Paragraph } from '@/components/Common';
import { EditIcon } from '@/components/Common/Icons';
import { useNationalityOptions } from '@/lib/reactHooks/useNationalityOptions';
import { kycNationalityOnChange } from '@/components/Form/utils';
import { dispatchFormAction } from '@/store/utils';
import { PaypalThemedProvider } from '@/styles';
import { verifyAndResetFullNames } from '@/components/SignupFields/KanaAndKanjiNames/utils';

export const NationalitySelect = (): JSX.Element => {
  const { country, locale } = getLocale();

  /* Ref */
  const editButtonRef = useRef<HTMLButtonElement>(null);
  /* Constants */
  const buttonId = 'kycCountryChangeButton';

  // Log the initial component mount
  useEffect(() => {
    logNationalityTextOnRenderFPTI({ country });
  }, []);

  // Subscribe to hook state
  const [countryOptions, decoratedOptions, nationality] =
    useNationalityOptions();

  /* Local State
    The PP React component "SelectionMenu" requires us to track
    the selected index of our country options, requiring us to manage
    state locally. This component is configured to automatically update
    its local state when the form state changes. A subscription to
    the form state resides in the useNationalityOptions hook.
  */
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [index, setIndex] = useState(
    countryOptions.findIndex(({ countryCode }) => countryCode === country),
  );

  // Local state will be a reflection of the form state
  useEffect(() => {
    const newIndex = countryOptions.findIndex(
      ({ key }) => key === (nationality || country),
    );
    setIndex(newIndex);
  }, [nationality, countryOptions]);

  /* This function is called when the user selects a country
    on the selection menu. It is memoized so that the function is not
    redeclared every re-render.
  */
  const onSelectionHandler = useMemo(() => {
    return (selectedIndex: number) => {
      const newCountry = countryOptions[selectedIndex]?.key;
      const oldCountry = countryOptions[index]?.key;
      if (newCountry === oldCountry) {
        // Do nothing other than close menu if same country is selected
        setIsMenuOpen(false);
        return;
      }
      // Fire FPTI log
      logSelectNationalityFPTI({ country: newCountry });
      // Update auxillary fields
      kycNationalityOnChange({ nationality: newCountry });
      // Update nationality field
      dispatchFormAction({
        field: 'nationality',
        payload: {
          displayError: false,
          isDirty: true,
          isValid: true,
          value: newCountry,
        },
      });
      setIsMenuOpen(false);
      editButtonRef?.current?.focus();
      verifyAndResetFullNames();
    };
  }, [countryOptions, country, locale, index]);

  // Show loading element if options not available
  if (!countryOptions || countryOptions.length === 0) {
    return <LoadingElement />;
  }

  return (
    <PaypalThemedProvider>
      <Flex>
        <Paragraph>
          {safeTrim(
            getContent('billing.changeNationality', {
              nationality: (
                <NationalitySpan>
                  {countryOptions[index]?.countryName}
                </NationalitySpan>
              ),
            }),
          )}
        </Paragraph>
        <SelectionMenuContainer>
          <EditButton
            aria-expanded={isMenuOpen}
            aria-haspopup="listbox"
            id={buttonId}
            data-testid={buttonId}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={editButtonRef}
            role="selection-menu-button"
            type="button"
          >
            <EditIcon />
          </EditButton>
          <SelectionMenu
            containerClassName="selectionMenu"
            data-testid="countryMenu"
            initialItemFocus={true}
            isOpen={isMenuOpen}
            itemClassName="menuOption"
            mobileTitle={getContent('billing.changeNationalityHeader')}
            onClose={() => setIsMenuOpen(false)}
            onSelection={onSelectionHandler}
            options={decoratedOptions}
            searchEnabled
            selectedIndex={index}
            theme="v2"
            triggerElementId={buttonId}
          />
        </SelectionMenuContainer>
      </Flex>
    </PaypalThemedProvider>
  );
};
